/**
 * @file src/views/VID/views/VidSessionKeyGuard.tsx
 * @exports VidSessionKeyGuard
 * @summary Blocks vID web routes until the router is ready and a session storage key can be derived; otherwise shows a persistent configuration error.
 * @remarks Keeps identity re-checks out of full-verification and feedback routes.
 *          Skips checks for landing and invite entry routes. Non-`/vid/` children pass through unchanged.
 */

import { useLocation, useSearch } from "@tanstack/react-router";
import { type ReactNode, useEffect } from "react";

import { WholePageLoader } from "src/components/Spinner";
import { paths } from "src/const";
import { navigateApp } from "src/lib/app-router";
import { vIDStateName } from "src/shame/shame-atoms";
import { isVidPathExemptFromPartitionGuard, resolveAndPersistVidPartitionKey } from "src/views/VID/vidStorageKey";

import { useSessionValue } from "./hooks/sessionHooks";
import { VidSessionConfigurationError } from "./VidSessionConfigurationError";

type VidSessionKeyGuardProps = {
	children: ReactNode;
};

const fullVerificationPaths = new Set<string>([
	paths.vidAlmostDone,
	paths.vidIdUpload,
	paths.vidInternationalIdType,
	paths.vidManualEntry,
	paths.vidPersonalInformation,
	paths.vidSelectIdType,
	paths.vidSignDisclosures,
	paths.vidSSN,
	paths.vidTooManyScanAttempts,
	paths.vidUsIdType,
]);

/** Prevents excluded pages from mounting, so they cannot request ID data or create a full order. */
const VidJourneyGuard = ({ children }: VidSessionKeyGuardProps) => {
	const { pathname } = useLocation();
	const search = useSearch({ strict: false }) as Record<string, string | string[] | undefined>;
	const isIdentityRecheck = useSessionValue(vIDStateName.IsIdentityRecheck);
	const order = useSessionValue(vIDStateName.VidWebOrder);
	const voucher = useSessionValue(vIDStateName.VidVoucher);
	const isFullVerificationPage =
		fullVerificationPaths.has(pathname) ||
		pathname === paths.vidAdditionalInformation ||
		pathname.startsWith(`${paths.vidAdditionalInformation}/`);
	let destination: string | null = null;
	if (isIdentityRecheck && (isFullVerificationPage || pathname === paths.vidFeedback)) {
		destination = order?.id ? paths.vidSuccess : paths.vidSelfie;
	}
	const voucherId = voucher?.id;
	useEffect(() => {
		if (destination) {
			void navigateApp({
				replace: true,
				search: { ...search, ...(voucherId ? { voucherId } : {}) },
				to: destination,
			});
		}
	}, [destination, search, voucherId]);

	return destination ? <WholePageLoader /> : <>{children}</>;
};

export const VidSessionKeyGuard = ({ children }: VidSessionKeyGuardProps) => {
	const location = useLocation();
	const search = useSearch({ strict: false }) as Record<string, string | string[] | undefined>;

	if (!location.pathname.startsWith("/vid/")) {
		return <>{children}</>;
	}

	if (isVidPathExemptFromPartitionGuard(location.pathname)) {
		return <>{children}</>;
	}

	if (!true) {
		return <WholePageLoader />;
	}

	const storageKey = resolveAndPersistVidPartitionKey(search as Record<string, unknown>, location.pathname);
	if (storageKey === null) {
		return <VidSessionConfigurationError />;
	}

	return <VidJourneyGuard>{children}</VidJourneyGuard>;
};
